import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
// NB: api.js imports emitAuthLogout from this file. We dynamically import it
// inside the effect below to avoid the circular-import landmine that would
// otherwise leave AuthContext as `undefined` to api.js at module-init time.

/**
 * AuthContext — single source of truth for admin authentication state.
 *
 * Round 2 refactor removes:
 *   - the `window.authLogout` global hack (replaced by event bus + direct
 *     context access through useApi)
 *   - hardcoded redirect in logout (navigates via event so React Router
 *     keeps ownership of history)
 *
 * Round 2 adds:
 *   - JWT decode → exposes the user payload with roles for RBAC
 *   - expiry check → auto-logout when token expires client-side (no need to
 *     wait for a 401)
 *   - hasRole / hasAnyRole helpers
 */

export const AuthContext = createContext(null);
const TOKEN_KEY = "adminToken";
const PERMS_KEY = "adminPermissions";
const AUTH_EVENT = "prosale:auth-logout";

/**
 * Safely decode a JWT payload (base64url-decoded JSON).
 * Returns null on malformed tokens — never throws.
 */
function decodeJwt(token) {
  if (!token || typeof token !== "string") return null;
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    // base64url → base64
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(b64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function isExpired(payload) {
  if (!payload || !payload.exp) return false;
  return Date.now() >= payload.exp * 1000;
}

export function AuthProvider({ children }) {
  const [userToken, setUserToken] = useState(null);
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const sessionTokenRef = useRef(null);

  // Initial token load
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    const payload = decodeJwt(token);
    if (token && payload && !isExpired(payload)) {
      setUserToken(token);
      setUser(payload);
      sessionTokenRef.current = token;
      // Old cached permissions are not bound to an account. Always refresh
      // before rendering protected screens, even when the token is valid.
      localStorage.removeItem(PERMS_KEY);
    } else if (token) {
      // stale token — clean up
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(PERMS_KEY);
    }
    if (!token || !payload || isExpired(payload)) setLoading(false);
  }, []);

  // Refresh on startup, focus and periodically so department/role changes
  // reach existing sessions. Previous accounts and older responses cannot win.
  useEffect(() => {
    if (!userToken) return;
    let cancelled = false;
    let requestId = 0;
    const isCurrent = (id) => !cancelled && sessionTokenRef.current === userToken && id === requestId;
    const refresh = async () => {
      const id = ++requestId;
      try {
        const { default: API } = await import("./api");
        if (!isCurrent(id)) return;
        const res = await API.get("/auth/permissions/me");
        if (!isCurrent(id)) return;
        const next = Array.isArray(res?.data?.permissions) ? res.data.permissions : [];
        localStorage.setItem(PERMS_KEY, JSON.stringify(next));
        setPermissions(next);
        if (Array.isArray(res?.data?.roles)) {
          setUser((current) => current ? {
            ...current, roles: res.data.roles, department: res.data.department ?? null,
          } : current);
        }
      } catch {
        if (isCurrent(id)) {
          localStorage.removeItem(PERMS_KEY);
          setPermissions([]);
        }
      } finally {
        if (isCurrent(id)) setLoading(false);
      }
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState !== "hidden") refresh();
    };
    refresh();
    const timer = setInterval(refreshWhenVisible, 60_000);
    window.addEventListener("focus", refreshWhenVisible);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener("focus", refreshWhenVisible);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [userToken]);

  // Reset all account-specific state on every login, including SSO logins
  // that do not include permissions in their response.
  const login = useCallback((token, extras = {}) => {
    const payload = decodeJwt(token);
    if (!payload || isExpired(payload)) {
      throw new Error("Received an invalid or expired token");
    }
    const sessionChanged = sessionTokenRef.current !== token;
    sessionTokenRef.current = token;
    localStorage.setItem(TOKEN_KEY, token);
    const next = Array.isArray(extras.permissions) ? extras.permissions : [];
    localStorage.setItem(PERMS_KEY, JSON.stringify(next));
    setPermissions(next);
    // Reusing the current token does not trigger another permissions effect.
    if (sessionChanged) setLoading(true);
    setUserToken(token);
    setUser(payload);
  }, []);

  const logout = useCallback(() => {
    sessionTokenRef.current = null;
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(PERMS_KEY);
    setUserToken(null);
    setUser(null);
    setPermissions([]);
    // React Router-friendly hard redirect to avoid stuck routes.
    // In the future you can replace this with navigate("/", { replace: true })
    // via a navigation-context integration.
    window.location.href = "/";
  }, []);

  // Axios interceptor fires an event instead of touching window directly.
  // This keeps context state the source of truth and plays nicely with SSR.
  useEffect(() => {
    const handler = () => logout();
    window.addEventListener(AUTH_EVENT, handler);
    return () => window.removeEventListener(AUTH_EVENT, handler);
  }, [logout]);

  // Client-side expiry watchdog: force-logout as soon as the JWT expires so
  // the user never experiences cascading 401s.
  useEffect(() => {
    if (!user?.exp) return;
    const msUntilExpiry = user.exp * 1000 - Date.now();
    if (msUntilExpiry <= 0) { logout(); return; }
    const t = setTimeout(logout, msUntilExpiry);
    return () => clearTimeout(t);
  }, [user, logout]);

  const hasRole = useCallback(
    (role) => {
      if (!user) return false;
      const roles = extractRoles(user);
      return roles.includes(role);
    },
    [user]
  );

  const hasAnyRole = useCallback(
    (...roles) => {
      if (!user) return false;
      const userRoles = extractRoles(user);
      return roles.some((r) => userRoles.includes(r));
    },
    [user]
  );

  /**
   * Permission gate matching the backend matcher in PermissionService:
   * exact key, "*" wildcard (god mode), or "feature.*" feature wildcard.
   * ADMIN role bypasses every check, mirroring the server-side bypass.
   */
  const hasPermission = useCallback(
    (key) => {
      if (!key) return false;
      if (hasRole("ADMIN")) return true;
      if (!Array.isArray(permissions) || permissions.length === 0) return false;
      if (permissions.includes("*")) return true;
      if (permissions.includes(key)) return true;
      const dot = key.indexOf(".");
      if (dot > 0 && permissions.includes(key.slice(0, dot) + ".*")) return true;
      return false;
    },
    [permissions, hasRole]
  );

  /** Replace the permission set in-place (used after the matrix is edited). */
  const setUserPermissions = useCallback((next) => {
    // A settings save from a previous account may finish after a new login.
    if (sessionTokenRef.current !== userToken || !Array.isArray(next)) return;
    localStorage.setItem(PERMS_KEY, JSON.stringify(next));
    setPermissions(next);
  }, [userToken]);

  const value = useMemo(
    () => ({
      userToken,
      user,
      permissions,
      loading,
      login,
      logout,
      hasRole,
      hasAnyRole,
      hasPermission,
      setUserPermissions,
    }),
    [
      userToken,
      user,
      permissions,
      loading,
      login,
      logout,
      hasRole,
      hasAnyRole,
      hasPermission,
      setUserPermissions,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Extract roles from a JWT payload. Supports several common shapes:
 *   - { roles: ["ADMIN", "MANAGER"] }
 *   - { authorities: [{authority: "ROLE_ADMIN"}, ...] }  (Spring style)
 *   - { role: "ADMIN" }
 */
function extractRoles(user) {
  if (!user) return [];
  if (Array.isArray(user.roles)) return user.roles.map(normalize);
  if (Array.isArray(user.authorities)) {
    return user.authorities
      .map((a) => (typeof a === "string" ? a : a?.authority))
      .filter(Boolean)
      .map(normalize);
  }
  if (typeof user.role === "string") return [normalize(user.role)];
  return [];
}

function normalize(role) {
  return String(role).replace(/^ROLE_/, "").toUpperCase();
}

/**
 * Fire the auth-logout event. Imported by api.js response interceptor so it
 * doesn't need to know about React contexts.
 */
export function emitAuthLogout() {
  window.dispatchEvent(new Event(AUTH_EVENT));
}
